import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import ApplicationStepHeader from "./components/ApplicationStepHeader";
import TenantStep from "./components/TenantStep";
import SpouseNextOfKinStep from "./components/SpouseNextOfKinStep";
import LandlordPetsStep from "./components/LandlordPetsStep";
import ReferencesFundsStep from "./components/ReferencesFundsStep";

const PublicRentalApplication = () => {
  const { propertyId } = useParams();
  const [step, setStep] = useState(1);
  const TOTAL_STEPS = 4;

  const [form, setForm] = useState({
    tenant: {
      firstName: "",
      lastName: "",
      email: "",
      phoneMobile: "",
      dateOfBirth: "",
      address: "",
      maritalStatus: "",
      numberOfChildren: "",
      childrenAges: "",
      employment: {
        employer: "",
        employerAddress: "",
        occupation: "",
        duration: "",
        monthlyIncome: "",
        workPhone: "",
      },
    },
    spouse: {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      age: "",
      address: "",
      email: "",
      phoneMobile: "",
      occupation: "",
      employment: {
        employer: "",
        employerAddress: "",
        monthlyIncome: "",
        workPhone: "",
      },
    },
    spouseNextOfKin: {
      name: "",
      relationship: "",
      address: "",
      phone: "",
    },
    nextOfKin: {
      name: "",
      relationship: "",
      address: "",
      phone: "",
    },
    currentResidence: {
      landlordName: "",
      landlordAddress: "",
      landlordPhone: "",
      howLongAtAddress: "",
      reasonForLeaving: "",
      rentalRate: "",
    },
    previousResidence: {
      landlordName: "",
      landlordAddress: "",
      landlordPhone: "",
      howLongAtAddress: "",
      reasonForLeaving: "",
    },
    pets: {
      hasPets: null,
      type: "",
      count: "",
    },
    vehicles: [
      {
        type: "",
        count: "",
        plateNumber: "",
      },
    ],
    references: [
      {
        name: "",
        address: "",
        phone: "",
        placeOfEmployment: "",
        relationship: "",
        employmentAddress: "",
      },
      {
        name: "",
        address: "",
        phone: "",
        placeOfEmployment: "",
        relationship: "",
        employmentAddress: "",
      },
    ],
    sourceOfFunds: {
      sources: [],
      otherDetails: "",
    },
    isCurrentlyRenting: null,
    hasPreviouslyRented: null,
    hasVehicles: null,
  });

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleTenantChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      tenant: {
        ...prev.tenant,
        [field]: value,
      },
    }));
  };

  const handleTenantEmploymentChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      tenant: {
        ...prev.tenant,
        employment: {
          ...prev.tenant.employment,
          [field]: value,
        },
      },
    }));
  };

  const handleSpouseChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      spouse: {
        ...prev.spouse,
        [field]: value,
      },
    }));
  };

  const handleSpouseEmploymentChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      spouse: {
        ...prev.spouse,
        employment: {
          ...prev.spouse.employment,
          [field]: value,
        },
      },
    }));
  };

  const handleSpouseNextOfKinChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      spouseNextOfKin: {
        ...prev.spouseNextOfKin,
        [field]: value,
      },
    }));
  };

  const handleNextOfKinChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      nextOfKin: {
        ...prev.nextOfKin,
        [field]: value,
      },
    }));
  };

  const handleCurrentResidenceChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      currentResidence: {
        ...prev.currentResidence,
        [field]: value,
      },
    }));
  };

  const handlePreviousResidenceChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      previousResidence: {
        ...prev.previousResidence,
        [field]: value,
      },
    }));
  };

  const handlePetsChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      pets: {
        ...prev.pets,
        [field]: value,
      },
    }));
  };

  const handleVehicleChange = (index, field, value) => {
    setForm((prev) => {
      const vehicles = [...prev.vehicles];
      vehicles[index] = {
        ...vehicles[index],
        [field]: value,
      };
      return { ...prev, vehicles };
    });
  };

  const handleReferenceChange = (index, field, value) => {
    setForm((prev) => {
      const references = [...prev.references];
      references[index] = {
        ...references[index],
        [field]: value,
      };
      return { ...prev, references };
    });
  };

  const handleSourceOfFundsChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      sourceOfFunds: {
        ...prev.sourceOfFunds,
        [field]: value,
      },
    }));
  };

  const handleSourceToggle = (value) => {
    setForm((prev) => {
      const current = prev.sourceOfFunds.sources;
      const updated = current.includes(value)
        ? current.filter((s) => s !== value)
        : [...current, value];
      return { ...prev, sourceOfFunds: { ...prev.sourceOfFunds, sources: updated } };
    });
  };

  const handleIsCurrentlyRentingChange = (value) => {
    setForm((prev) => ({ ...prev, isCurrentlyRenting: value }));
  };

  const handleHasPreviouslyRentedChange = (value) => {
    setForm((prev) => ({ ...prev, hasPreviouslyRented: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);

    // If not on final step, just advance the stepper
    if (step < TOTAL_STEPS) {
      setStep((prev) => Math.min(TOTAL_STEPS, prev + 1));
      window.scrollTo(0, 0);
      return;
    }

    setSubmitting(true);

    try {
      // Build payload with normalisation and skip optional groups when empty
      const payload = {
        propertyId,
        // TENANT
        tenant: {
          ...form.tenant,
          numberOfChildren: form.tenant.numberOfChildren
            ? Number(form.tenant.numberOfChildren)
            : undefined,
          employment: {
            ...form.tenant.employment,
            monthlyIncome: form.tenant.employment.monthlyIncome
              ? Number(form.tenant.employment.monthlyIncome)
              : undefined,
          },
        },
        // SPOUSE
        spouse: {
          ...form.spouse,
          age: form.spouse.age ? Number(form.spouse.age) : undefined,
          employment: {
            ...form.spouse.employment,
            monthlyIncome: form.spouse.employment.monthlyIncome
              ? Number(form.spouse.employment.monthlyIncome)
              : undefined,
          },
        },
        // RESIDENCE HISTORY
        currentResidence: {
          ...form.currentResidence,
          rentalRate: form.currentResidence.rentalRate
            ? Number(form.currentResidence.rentalRate)
            : undefined,
        },
        previousResidence: {
          ...form.previousResidence,
        },
        // SPOUSE NEXT OF KIN
        spouseNextOfKin: {
          ...form.spouseNextOfKin,
        },
        // NEXT OF KIN
        nextOfKin: {
          ...form.nextOfKin,
        },
        // REFERENCES & FUNDS
        references: form.references,
        sourceOfFunds: {
          sources: form.sourceOfFunds.sources,
          otherDetails: form.sourceOfFunds.sources.includes("other")
            ? form.sourceOfFunds.otherDetails
            : undefined,
        },
      };

      // Only include pets if user actually provided something
      const hasPetData =
        form.pets.hasPets ||
        (form.pets.type && form.pets.type.trim() !== "") ||
        (form.pets.count && String(form.pets.count).trim() !== "");

      if (hasPetData) {
        payload.pets = {
          ...form.pets,
          count: form.pets.count ? Number(form.pets.count) : undefined,
        };
      }

      // Only include vehicles that have any data
      const normalisedVehicles = form.vehicles
        .map((v) => ({
          ...v,
          count: v.count ? Number(v.count) : undefined,
        }))
        .filter(
          (v) =>
            (v.type && v.type.trim() !== "") ||
            (v.plateNumber && v.plateNumber.trim() !== "") ||
            typeof v.count === "number"
        );

      if (normalisedVehicles.length > 0) {
        payload.vehicles = normalisedVehicles;
      }

      const res = await fetch(API_ENDPOINTS.applications.submit, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        const message =
          data?.message ||
          (Array.isArray(data?.errors) && data.errors[0]?.msg) ||
          "Error submitting application";
        setResult({ success: false, message });
      } else {
        setResult({
          success: true,
          referenceId: data.data.applicationId,
        });
      }
    } catch (err) {
      console.error("Error submitting public rental application", err);
      setResult({
        success: false,
        message: "Unexpected error submitting application. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.success) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f0f2f5" }}>
        <div className="container py-5">
          <div className="card border-0 shadow-sm mx-auto p-5 text-center" style={{ maxWidth: 600 }}>
            <div className="mb-3" style={{ fontSize: "3rem" }}>&#10003;</div>
            <h2 className="fw-bold mb-3">Application Submitted</h2>
            <p className="text-muted mb-4">
              Thank you for submitting your rental application. Your application is now being reviewed.
            </p>
            <div className="bg-light rounded p-3 mb-4">
              <small className="text-muted d-block mb-1">Your Reference ID</small>
              <h4 className="fw-bold mb-0">{result.referenceId}</h4>
            </div>
            <p className="text-muted small mb-0">
              For any questions or concerns, please contact <strong>Inside Real Estate</strong>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f0f2f5" }}>
    <div className="container py-4">
      <h2 className="mb-3">Rental Application</h2>
      <p className="text-muted mb-4">
        Please complete the form below to apply for this rental property.
      </p>

      <form onSubmit={handleSubmit} className="card p-3">
        <ApplicationStepHeader step={step} totalSteps={TOTAL_STEPS} />

        {step === 1 && (
          <TenantStep
            tenant={form.tenant}
            onTenantChange={handleTenantChange}
            onEmploymentChange={handleTenantEmploymentChange}
          />
        )}

        {step === 2 && (
          <SpouseNextOfKinStep
            spouse={form.spouse}
            spouseNextOfKin={form.spouseNextOfKin}
            nextOfKin={form.nextOfKin}
            maritalStatus={form.tenant.maritalStatus}
            onSpouseChange={handleSpouseChange}
            onSpouseEmploymentChange={handleSpouseEmploymentChange}
            onSpouseNextOfKinChange={handleSpouseNextOfKinChange}
            onNextOfKinChange={handleNextOfKinChange}
          />
        )}

        {step === 3 && (
          <LandlordPetsStep
            isCurrentlyRenting={form.isCurrentlyRenting}
            onIsCurrentlyRentingChange={handleIsCurrentlyRentingChange}
            hasPreviouslyRented={form.hasPreviouslyRented}
            onHasPreviouslyRentedChange={handleHasPreviouslyRentedChange}
            currentResidence={form.currentResidence}
            previousResidence={form.previousResidence}
            pets={form.pets}
            vehicles={form.vehicles}
            hasVehicles={form.hasVehicles}
            onHasVehiclesChange={(val) => setForm((prev) => ({ ...prev, hasVehicles: val }))}
            onCurrentResidenceChange={handleCurrentResidenceChange}
            onPreviousResidenceChange={handlePreviousResidenceChange}
            onPetsChange={handlePetsChange}
            onVehicleChange={handleVehicleChange}
          />
        )}

        {step === 4 && (
          <ReferencesFundsStep
            references={form.references}
            sourceOfFunds={form.sourceOfFunds}
            onReferenceChange={handleReferenceChange}
            onSourceOfFundsChange={handleSourceOfFundsChange}
            onSourceToggle={handleSourceToggle}
          />
        )}

        <div className="mt-4 d-flex justify-content-between">
          <div>
            {step > 1 && (
              <button
                type="button"
                className="btn btn-outline-secondary me-2"
                onClick={() => {
                  setStep((prev) => Math.max(1, prev - 1));
                  window.scrollTo(0, 0);
                }}
              >
                Back
              </button>
            )}
          </div>
          <div>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Submitting..." : step < TOTAL_STEPS ? "Next" : "Submit Application"}
            </button>
          </div>
        </div>

        {result && !result.success && (
          <div className="alert alert-danger mt-3">
            {result.message}
          </div>
        )}
      </form>
    </div>
    </div>
  );
};

export default PublicRentalApplication;
